// ============================================================
// Datei: ShiftTable.jsx
// PURPOSE:
// - Zentrale Logik für PUBLIC / PRIVATE Umschaltung
// - Modal-gesteuertes Publizieren mit Slot-Auswahl
// - Mobile & Desktop kompatibel
// - Sortierung strikt nach day_of_deployment_id
//   (numerisch, robust gegen DTO/Entity-Mix)
//
// ÄNDERUNGEN:
// - filterFn als optionales Prop ergänzt
// - Backend-Refresh lädt weiterhin das ganze Department,
//   filtert danach aber sauber auf die gewünschte Teilmenge
// - dadurch bleiben Unterbereiche wie Eingangskasse / VIP-Kasse
//   korrekt getrennt und aktualisieren sich trotzdem automatisch
// ============================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import Api from "../../Api";
import useMediaQuery from "../../hooks/useMediaQuery";
import ShiftTableDesktop from "./ShiftTableDesktop";
import ShiftTableMobile from "./ShiftTableMobile";
import PublicSlotsModal from "../modals/PublicSlotsModal";
import ConfirmVisibilityModal from "../modals/ConfirmVisibilityModal";

// ------------------------------------------------------------
// Hilfsfunktion:
// Sortiert Schichten robust nach day_of_deployment_id
// ------------------------------------------------------------
const sortShiftsByDayId = (inputShifts = []) => {
	return [...inputShifts].sort((a, b) => {
		const dayIdA = Number(
			a.dayOfDeployment?.dayOfDeploymentId ??
			a.dayOfDeploymentId ??
			9999
		);

		const dayIdB = Number(
			b.dayOfDeployment?.dayOfDeploymentId ??
			b.dayOfDeploymentId ??
			9999
		);

		return dayIdA - dayIdB;
	});
};

// ------------------------------------------------------------
// Hilfsfunktion:
// Wendet optional einen externen Filter an
// ------------------------------------------------------------
const applyOptionalFilter = (inputShifts = [], filterFn = null) => {
	if (typeof filterFn !== "function") {
		return inputShifts;
	}

	return inputShifts.filter(filterFn);
};

const ShiftTable = ({
	shifts = [],
	departmentName = "",
	autoRefresh = true,
	refreshIntervalMs = 5000,
	filterFn = null
}) => {
	const isMobile = useMediaQuery("(max-width: 768px)");

	// ------------------------------------------------------------
	// Lokaler State (UI-optimiert, Backend bleibt Source of Truth)
	// ------------------------------------------------------------
	const [localShifts, setLocalShifts] = useState([]);

	const [publishOpen, setPublishOpen] = useState(false);
	const [publishShiftId, setPublishShiftId] = useState(null);

	const [confirmOpen, setConfirmOpen] = useState(false);
	const [confirmShiftId, setConfirmShiftId] = useState(null);

	// ------------------------------------------------------------
	// Aktuelles Department bestimmen
	// - zuerst Prop
	// - sonst localStorage
	// ------------------------------------------------------------
	const effectiveDepartment = useMemo(() => {
		return departmentName || localStorage.getItem("department") || "";
	}, [departmentName]);

	// ------------------------------------------------------------
	// Übergebene Props übernehmen, optional filtern und sortieren
	// ------------------------------------------------------------
	useEffect(() => {
		const filtered = applyOptionalFilter(shifts, filterFn);
		setLocalShifts(sortShiftsByDayId(filtered));
	}, [shifts, filterFn]);

	// ============================================================
	// BACKEND-REFRESH
	// Lädt die aktuellen Ressort-Schichten neu
	// und filtert danach optional auf die gewünschte Teilmenge
	// ============================================================
	const refreshShiftsFromBackend = useCallback(async () => {
		if (!effectiveDepartment) {
			return;
		}

		try {
			const encodedDepartment = encodeURIComponent(effectiveDepartment);

			const { data } = await Api.get(
				`/deployments/department/${encodedDepartment}/shifts`
			);

			if (Array.isArray(data)) {
				const filtered = applyOptionalFilter(data, filterFn);
				setLocalShifts(sortShiftsByDayId(filtered));
			}
		} catch (err) {
			console.error("Fehler beim Aktualisieren der Schichttabelle:", err);
		}
	}, [effectiveDepartment, filterFn]);

	// ============================================================
	// AUTO-REFRESH INTERVAL
	// Nur aktiv, wenn autoRefresh=true
	// ============================================================
	useEffect(() => {
		if (!autoRefresh || !effectiveDepartment) {
			return undefined;
		}

		refreshShiftsFromBackend();

		const intervalId = window.setInterval(() => {
			refreshShiftsFromBackend();
		}, refreshIntervalMs);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [
		autoRefresh,
		effectiveDepartment,
		refreshIntervalMs,
		refreshShiftsFromBackend
	]);

	// ============================================================
	// API: PRIVATE setzen (Slots = 0)
	// ============================================================
	const setPrivate = async (shiftId) => {
		try {
			const { data } = await Api.patch(
				`/shifts/${shiftId}/visibility`,
				{ publicSlots: 0 }
			);

			setLocalShifts((prev) =>
				prev.map((s) =>
					s.shiftId === shiftId ? { ...s, ...data } : s
				)
			);

			if (autoRefresh) {
				await refreshShiftsFromBackend();
			}
		} catch (err) {
			console.error("Fehler beim De-Publizieren:", err);
		}
	};

	// ============================================================
	// API: PUBLIC setzen (Slots > 0)
	// ============================================================
	const setPublic = async (shiftId, slots) => {
		try {
			const { data } = await Api.patch(
				`/shifts/${shiftId}/visibility`,
				{ publicSlots: slots }
			);

			setLocalShifts((prev) =>
				prev.map((s) =>
					s.shiftId === shiftId ? { ...s, ...data } : s
				)
			);

			if (autoRefresh) {
				await refreshShiftsFromBackend();
			}
		} catch (err) {
			console.error("Fehler beim Publizieren:", err);
		}
	};

	// ============================================================
	// Toggle PUBLIC ↔ PRIVATE
	// ============================================================
	const toggleVisibility = (shift) => {
		if (shift.shiftVisibility === "PRIVATE") {
			setPublishShiftId(shift.shiftId);
			setPublishOpen(true);
			return;
		}

		if (shift.shiftVisibility === "PUBLIC" && shift.publicSlots > 0) {
			setConfirmShiftId(shift.shiftId);
			setConfirmOpen(true);
			return;
		}

		setPrivate(shift.shiftId);
	};

	return (
		<>
			{publishOpen && (() => {
				const shift = localShifts.find((s) => s.shiftId === publishShiftId);
				if (!shift) return null;

				const maxSlots =
					Number(shift.requestedAmountOfVolunteers || 0) -
					Number(shift.bookedShift || 0);

				return (
					<PublicSlotsModal
						maxSlots={Math.max(0, maxSlots)}
						onCancel={() => {
							setPublishOpen(false);
							setPublishShiftId(null);
						}}
						onSubmit={async (slots) => {
							await setPublic(publishShiftId, slots);
							setPublishOpen(false);
							setPublishShiftId(null);
						}}
					/>
				);
			})()}

			{confirmOpen && (
				<ConfirmVisibilityModal
					onCancel={() => {
						setConfirmOpen(false);
						setConfirmShiftId(null);
					}}
					onConfirm={async () => {
						await setPrivate(confirmShiftId);
						setConfirmOpen(false);
						setConfirmShiftId(null);
					}}
				/>
			)}

			{isMobile ? (
				<ShiftTableMobile
					shifts={localShifts}
					onToggleVisibility={toggleVisibility}
				/>
			) : (
				<ShiftTableDesktop
					shifts={localShifts}
					onToggleVisibility={toggleVisibility}
				/>
			)}
		</>
	);
};

export default ShiftTable;